import { buildReviewHierarchy, type DtoolsStagingLine, type DtoolsStagingSession } from "./dtoolsJsonStaging";
import { DTOOLS_JSON_SOURCE } from "./dtoolsSiJsonImport";
import type { UploadRecoveryLineItem } from "./proposalUploadDoctrine";
import { DTOOLS_JSON_PROPOSAL_SOURCE } from "./proposalSources";
import type { DtoolsDisplaySettings } from "./dtoolsDisplaySettings";
import type { DtoolsSystemContent } from "./dtoolsProposalContent";
import type { DtoolsProposalPresentation } from "./dtoolsProposalPresentation";
import {
  normaliseDtoolsReviewFulfilmentTimeline,
  readDtoolsReviewAssembly,
  reviewAssemblyToConfirmMetadata,
} from "./dtoolsReviewAssembly";
import {
  resolveLineDisplayImage,
  resolveStagingLineVisibleDescription,
} from "./dtoolsCatalogueMatch";
import { fallbackProductDisplayName } from "./productDisplayName";
import {
  buildDtoolsPersistedItemRawText,
  buildSafeDtoolsHeaderExtractedText,
  buildSafeDtoolsParsedText,
} from "./dtoolsSafeProposalMetadata";
import { utf8ByteLength } from "./utf8ByteLength";
import { applyEquipmentPercentagePricingToStagingLines } from "./equipmentPercentagePricing";
import { EMPTY_DTOOLS_DISPLAY_SETTINGS, type DtoolsPortalPaymentTerm } from "./dtoolsDisplaySettings";
import { mapDtoolsPaymentTermsForPortal } from "./dtoolsDisplaySettings";
import { applyHardwareMarkupToLines } from "./proposalHardwareMarkup";

export type { DtoolsPortalPaymentTerm } from "./dtoolsDisplaySettings";
export { mapDtoolsPaymentTermsForPortal } from "./dtoolsDisplaySettings";

function compactHierarchyLineFields(l: DtoolsStagingLine): Record<string, unknown> {
  return {
    id: l.id,
    dtoolsItemId: l.dtoolsItemId,
    bucket: l.bucket,
    systemId: l.systemId,
    systemName: l.systemName,
    locationId: l.locationId,
    roomName: l.roomName,
    locationName: l.locationName ?? null,
    locationFullName: l.locationFullName ?? null,
    parentLocationId: l.parentLocationId ?? null,
    parentId: l.parentId,
    isAccessory: l.isAccessory,
    catalogueKey: l.catalogueKey,
    catalogId: l.catalogId,
    componentId: l.componentId,
    sortOrder: l.sortOrder,
  };
}

function compactHierarchyFooter(lines: DtoolsStagingLine[]) {
  const hierarchy = buildReviewHierarchy(lines);
  return {
    adjustmentIds: hierarchy.footer.adjustments.map((l) => l.id),
    optionalIds: hierarchy.footer.optional.map((l) => l.id),
    nonBillableIds: hierarchy.footer.nonBillable.map((l) => l.id),
  };
}

/**
 * Persisted on confirm — lines + footer only (no nested systems tree).
 * Full tree is rebuilt from proposal_items.rawText + lines[] at read time.
 */
export function buildCompactDtoolsHierarchySnapshotFromLines(
  lines: DtoolsStagingLine[],
  meta?: { sessionId?: string; fileName?: string; proposalRef?: string | null },
): Record<string, unknown> {
  return {
    version: 1,
    compact: true,
    sessionId: meta?.sessionId ?? "revise",
    fileName: meta?.fileName ?? null,
    proposalRef: meta?.proposalRef ?? null,
    footer: compactHierarchyFooter(lines),
    lines: lines.map(compactHierarchyLineFields),
  };
}

/** Compact hierarchy + line index for proposal_headers.parsedMetadataJson on confirm. */
export function buildDtoolsImportHierarchySnapshot(session: DtoolsStagingSession): Record<string, unknown> {
  return {
    ...buildCompactDtoolsHierarchySnapshotFromLines(session.lines, {
      sessionId: session.sessionId,
      fileName: session.fileName ?? undefined,
      proposalRef: session.parseResult.project.proposalRef,
    }),
    locations: (session.parseResult.locations ?? []).map((l) => ({
      id: l.id,
      name: l.name,
      fullName: l.fullName,
      parentId: l.parentId,
    })),
  };
}

/** Full hierarchy snapshot (includes systems tree) — used when saving revisions. */
export function buildDtoolsHierarchySnapshotFromLines(
  lines: DtoolsStagingLine[],
  meta?: { sessionId?: string; fileName?: string; proposalRef?: string | null },
): Record<string, unknown> {
  const hierarchy = buildReviewHierarchy(lines);
  return {
    version: 1,
    sessionId: meta?.sessionId ?? "revise",
    fileName: meta?.fileName ?? null,
    proposalRef: meta?.proposalRef ?? null,
    systems: hierarchy.systems.map((s) => ({
      systemId: s.systemId,
      name: s.name,
      rooms: s.rooms.map((r) => ({
        locationId: r.locationId,
        name: r.name,
        locationName: r.locationName ?? null,
        locationFullName: r.locationFullName ?? null,
        parentLocationId: r.parentLocationId ?? null,
        primaries: r.primaries.map((p) => ({
          lineId: p.line.id,
          accessoryIds: p.accessories.map((a) => a.id),
        })),
      })),
      systemLevelPrimaries: s.systemLevelPrimaries.map((p) => ({
        lineId: p.line.id,
        accessoryIds: p.accessories.map((a) => a.id),
      })),
    })),
    footer: compactHierarchyFooter(lines),
    lines: lines.map(compactHierarchyLineFields),
  };
}

/** Line buckets included in Portal proposal_items on confirm. */
export const DTOOLS_CONFIRM_IMPORT_BUCKETS = new Set([
  "commercial",
  "discount",
  "service",
  "cable",
] as const);

export type DtoolsConfirmUploadItem = UploadRecoveryLineItem & {
  imageUrlSnapshot?: string | null;
  displayName?: string | null;
  displayNameManuallyEdited?: boolean;
  pricingMethod?: "fixed" | "percentage_of_equipment" | string | null;
  percentageOfEquipment?: number | null;
};

export function isDtoolsStagingLineConfirmable(line: DtoolsStagingLine): boolean {
  return (DTOOLS_CONFIRM_IMPORT_BUCKETS as ReadonlySet<string>).has(line.bucket);
}

function lineDisplayName(line: DtoolsStagingLine): string {
  const brandModel = [line.manufacturer, line.model].filter(Boolean).join(" ").trim();
  if (brandModel) return brandModel.slice(0, 512);
  const polished = String(line.polishedDescription ?? "").trim();
  if (polished) return polished.slice(0, 512);
  const raw = String(line.dtoolsDescription ?? "").trim();
  return raw.slice(0, 512) || "Line item";
}

export function buildDtoolsStagingLineRawText(line: DtoolsStagingLine): string {
  return buildDtoolsPersistedItemRawText(line);
}

export function stagingLineToConfirmItem(line: DtoolsStagingLine): DtoolsConfirmUploadItem {
  const description = resolveStagingLineVisibleDescription(line) || lineDisplayName(line);
  const imageUrl = resolveLineDisplayImage(line.imageUrl, line);
  const rawText = buildDtoolsPersistedItemRawText(line);

  return {
    rawText,
    name: lineDisplayName(line),
    brand: line.manufacturer,
    model: line.model,
    displayName:
      line.displayName?.trim() ||
      fallbackProductDisplayName({
        brand: line.manufacturer,
        model: line.model,
        description: line.dtoolsDescription,
      }),
    displayNameManuallyEdited: line.displayNameManuallyEdited === true,
    category: line.category,
    description: description.slice(0, 20000),
    quantity: Math.max(1, Math.round(line.quantity || 1)),
    unitPrice: Math.max(0, Number(line.unitPrice) || 0),
    totalPrice: Math.max(0, Number(line.lineTotal) || 0),
    extractionConfidence: 1,
    imageUrlSnapshot: imageUrl,
    cleoDescription: String(line.cleoDescription ?? "").trim() || null,
    customerExplanation: String(line.customerExplanation ?? "").trim() || null,
    pricingMethod: line.pricingMethod ?? null,
    percentageOfEquipment:
      line.percentageOfEquipment != null && Number.isFinite(Number(line.percentageOfEquipment))
        ? Number(line.percentageOfEquipment)
        : null,
  };
}

export function selectConfirmableStagingLines(lines: DtoolsStagingLine[]): DtoolsStagingLine[] {
  return [...lines]
    .filter(isDtoolsStagingLineConfirmable)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/** Collect D-Tools item ids persisted in a compact hierarchy snapshot at import. */
export function collectImportedHierarchyLineIds(parsedMetadataJson?: unknown): Set<string> | null {
  const meta =
    parsedMetadataJson !== null && typeof parsedMetadataJson === "object"
      ? (parsedMetadataJson as Record<string, unknown>)
      : null;
  const snapshot =
    meta?.dtoolsHierarchySnapshot !== null && typeof meta?.dtoolsHierarchySnapshot === "object"
      ? (meta.dtoolsHierarchySnapshot as Record<string, unknown>)
      : null;
  const rows = snapshot?.lines;
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const ids = new Set<string>();
  for (const entry of rows) {
    if (!entry || typeof entry !== "object") continue;
    const row = entry as Record<string, unknown>;
    const id = String(row.id ?? row.dtoolsItemId ?? "").trim();
    if (id) ids.add(id);
    const dtoolsItemId = String(row.dtoolsItemId ?? "").trim();
    if (dtoolsItemId) ids.add(dtoolsItemId);
  }
  return ids.size > 0 ? ids : null;
}

/**
 * Uploaded lines eligible for revision compare — same confirmable buckets as import,
 * excluding orphan zero-total rows that were never persisted in the hierarchy snapshot.
 */
export function selectDtoolsRevisionCompareUploadedLines(
  lines: DtoolsStagingLine[],
  importedHierarchyLineIds?: ReadonlySet<string> | null,
): DtoolsStagingLine[] {
  const confirmable = selectConfirmableStagingLines(lines);
  if (!importedHierarchyLineIds || importedHierarchyLineIds.size === 0) {
    return confirmable;
  }
  return confirmable.filter((line) => {
    const id = line.dtoolsItemId?.trim();
    if (!id) return true;
    if (importedHierarchyLineIds.has(id)) return true;
    return Math.round((Number(line.lineTotal) || 0) * 100) / 100 > 0;
  });
}

export type ConfirmUploadedProposalFromDtoolsInput = {
  proposalSource: typeof DTOOLS_JSON_PROPOSAL_SOURCE;
  rawExtractedText: string;
  parsedText: string;
  metadata: {
    proposalName: string;
    projectNumber: string;
    customerName: string;
    customerId?: number | null;
    address?: string | null;
    phone?: string | null;
    grandTotal: number;
    parseWarnings?: string[];
    parseConfidence: number;
    inferredClassification?: string | null;
    parserStrategy: string;
    parserVersion: string;
    reviewRequired?: boolean;
    blockReasons?: string[];
    totalsCheck?: {
      headerTotal: number | null;
      itemsTotal: number;
      absoluteDelta: number;
      relativeDelta: number;
      severeMismatch: boolean;
    };
    extractionTimestamps?: { extractedAt: string; confirmedAt?: string };
    dtoolsStagingSessionId?: string;
    dtoolsHierarchySnapshot?: Record<string, unknown>;
    dtoolsPaymentTerms?: DtoolsPortalPaymentTerm[];
    dtoolsDisplaySettings?: DtoolsDisplaySettings;
    dtoolsSystemContent?: Record<string, DtoolsSystemContent>;
    dtoolsProposalPresentation?: DtoolsProposalPresentation;
    dtoolsSystemClassifications?: import("./dtoolsSystemClassification").DtoolsSystemClassificationsMap;
    dtoolsTabModelVersion?: import("./dtoolsSystemClassification").DtoolsTabModelVersion;
    dtoolsNegotiatedPricing?: import("./dtoolsReviewAssembly").DtoolsReviewAssemblyNegotiatedPricing;
    proposalTitleUserEdited?: boolean;
  };
  items: DtoolsConfirmUploadItem[];
  /** Top-level mutation fields — server revalidates via fulfilment timeline authority. */
  fulfilmentTimelineType?: "unset" | "asap" | "month_year";
  targetFulfilmentMonth?: string | null;
  procurementTrackingOrigin?: "portal_native" | "legacy_import";
};

export function buildConfirmUploadedProposalInputFromDtoolsStaging(
  session: DtoolsStagingSession,
  options?: {
    customerId?: number | null;
    forceProjectNumber?: string;
    /** When true and timing was not explicitly re-selected, omit timeline fields so the server inherits. */
    allowInheritOnRevision?: boolean;
  },
): ConfirmUploadedProposalFromDtoolsInput {
  const { parseResult: result } = session;
  const assembly = readDtoolsReviewAssembly(session);
  const displaySettings = assembly.displaySettings ?? EMPTY_DTOOLS_DISPLAY_SETTINGS;
  const hierarchy = buildReviewHierarchy(session.lines);
  const percentagePricedLines = applyEquipmentPercentagePricingToStagingLines(
    session.lines,
    hierarchy.systems,
    displaySettings,
  ) as DtoolsStagingLine[];
  const pricedLines = applyHardwareMarkupToLines(percentagePricedLines, {
    markupEnabled: assembly.negotiatedPricing?.markupEnabled === true,
    markupPercentage: Number(assembly.negotiatedPricing?.markupPercentage ?? 0),
  });
  const confirmLines = selectConfirmableStagingLines(pricedLines);
  const items = confirmLines.map(stagingLineToConfirmItem);
  const itemsTotal = items.reduce((sum, i) => sum + Number(i.totalPrice ?? 0), 0);
  const headerTotal = Number(result.project.projectTotal ?? 0);
  const absoluteDelta = Math.abs(itemsTotal - headerTotal);
  const relativeDelta = headerTotal > 0 ? absoluteDelta / headerTotal : 0;
  const recon = result.reconciliation;

  const projectNumber =
    (options?.forceProjectNumber ?? result.project.proposalRef ?? "").trim() ||
    `DTOOLS-${session.sessionId.slice(0, 12)}`;

  const headerTextInput = {
    proposalName: result.project.proposalName,
    projectNumber,
    sessionId: session.sessionId,
    fileName: session.fileName,
    itemCount: items.length,
    parserVersion: result.version,
    rawJsonByteSize: utf8ByteLength(String(session.rawJson ?? "")),
  };

  const parseWarnings = [...(result.warnings ?? [])];
  if (items.length === 0) {
    parseWarnings.push("No billable staging lines selected for import.");
  }
  if (recon && !recon.reconciled) {
    parseWarnings.push("Staging totals did not fully reconcile with D-Tools header — review before sending to customer.");
  }

  const assemblyMeta = reviewAssemblyToConfirmMetadata(assembly);
  const resolvedProposalName =
    String(assembly.presentation?.proposalTitle ?? "").trim() ||
    String(result.project.proposalName ?? "").trim() ||
    "Uploaded Proposal";
  const timeline = normaliseDtoolsReviewFulfilmentTimeline(assembly.fulfilmentTimeline);
  const allowInheritOnRevision = options?.allowInheritOnRevision === true;
  const shouldSendTimeline =
    timeline.explicitlySelected ||
    (!allowInheritOnRevision &&
      (timeline.fulfilmentTimelineType === "asap" ||
        timeline.fulfilmentTimelineType === "month_year" ||
        timeline.fulfilmentTimelineType === "unset"));

  return {
    proposalSource: DTOOLS_JSON_PROPOSAL_SOURCE,
    rawExtractedText: buildSafeDtoolsHeaderExtractedText(headerTextInput),
    parsedText: buildSafeDtoolsParsedText(headerTextInput),
    metadata: {
      proposalName: resolvedProposalName,
      projectNumber,
      customerName: result.customer?.clientName || "Unknown customer",
      customerId: options?.customerId ?? null,
      address: result.customer?.siteAddress || undefined,
      grandTotal: headerTotal,
      parseWarnings,
      parseConfidence: 1,
      inferredClassification: "project",
      parserStrategy: DTOOLS_JSON_SOURCE,
      parserVersion: result.version,
      reviewRequired: Boolean(recon && !recon.reconciled),
      blockReasons: items.length === 0 ? ["NO_VALID_LINE_ITEMS"] : [],
      totalsCheck: {
        headerTotal,
        itemsTotal,
        absoluteDelta,
        relativeDelta,
        severeMismatch: recon?.severeMismatch ?? relativeDelta > 0.02,
      },
      extractionTimestamps: {
        extractedAt: session.createdAt,
        confirmedAt: new Date().toISOString(),
      },
      dtoolsStagingSessionId: session.sessionId,
      dtoolsHierarchySnapshot: buildDtoolsImportHierarchySnapshot({ ...session, lines: pricedLines }),
      dtoolsPaymentTerms: assemblyMeta.dtoolsPaymentTerms ?? mapDtoolsPaymentTermsForPortal(result.paymentTerms),
      dtoolsDisplaySettings: assemblyMeta.dtoolsDisplaySettings,
      dtoolsSystemContent: assemblyMeta.dtoolsSystemContent,
      dtoolsProposalPresentation: assemblyMeta.dtoolsProposalPresentation,
      dtoolsSystemClassifications: assemblyMeta.dtoolsSystemClassifications,
      dtoolsTabModelVersion: assemblyMeta.dtoolsTabModelVersion,
      dtoolsNegotiatedPricing: assemblyMeta.dtoolsNegotiatedPricing,
      proposalTitleUserEdited: assembly.proposalTitleUserEdited === true,
    },
    items,
    ...(shouldSendTimeline
      ? {
          fulfilmentTimelineType: timeline.fulfilmentTimelineType,
          targetFulfilmentMonth:
            timeline.fulfilmentTimelineType === "asap" ? null : timeline.targetFulfilmentMonth,
          procurementTrackingOrigin: timeline.procurementTrackingOrigin,
        }
      : {}),
  };
}
